import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Save, Loader2, Upload, Trophy, RefreshCw, Check, X, Eraser, PenLine } from "lucide-react";
import { toast } from "sonner";
import {
  fetchArtists, createArtist, updateArtist, deleteArtist, uploadImage, mediaUrl,
  fetchRankingsSyncStatus, triggerRankingsSync, fetchRankingSuggestions,
  approveSuggestion, approveAllSuggestions, dismissSuggestion, removeUnrankedArtists, createMoversDraft,
} from "@/lib/api";

const inputCls = "w-full mt-1 bg-background border border-white/15 px-3 py-2 text-foreground text-sm placeholder:text-muted-foreground/60 focus:border-signal outline-none font-sans";
const label = "font-mono text-[10px] uppercase tracking-widest text-muted-foreground";
const btnPrimary = "inline-flex items-center gap-2 bg-signal text-white font-mono text-[11px] uppercase tracking-widest px-4 py-2 hover:bg-signal-hover transition-colors disabled:opacity-50";
const btnGhost = "inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest border border-white/15 px-4 py-2 text-muted-foreground hover:border-signal hover:text-signal transition-colors disabled:opacity-50";
const emptyArtist = () => ({ name: "", title: "", city: "", image: "", bio: "" });
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "never");

function ScorePills({ scores = {}, overall }) {
  return (
    <div className="flex flex-wrap gap-2 font-mono text-[10px] uppercase tracking-widest">
      <span className="border border-signal/60 text-signal px-2 py-1">Overall {overall ?? 0}</span>
      <span className="border border-white/15 px-2 py-1 text-foreground/80">Charts {scores.charts ?? 0}</span>
      <span className="border border-white/15 px-2 py-1 text-foreground/80">Press {scores.press ?? 0}</span>
      <span className="border border-white/15 px-2 py-1 text-foreground/80">Fans {scores.fans ?? 0}</span>
    </div>
  );
}

function SyncPanel({ status, onSync, syncing, onMovers, writing }) {
  const running = !!status?.running;
  return (
    <div className="border border-signal/40 bg-card p-5 grid md:grid-cols-[1fr_auto] gap-4 items-center" data-testid="sync-panel">
      <div>
        <div className={label}>Automatic chart sync · monthly on the 1st</div>
        <div className="font-display text-lg mt-1" data-testid="sync-last">
          Last sync: {fmtDate(status?.last_success)}
          {running && <span className="text-signal ml-3 font-mono text-xs uppercase tracking-widest">· running…</span>}
          {status?.status === "error" && <span className="text-signal ml-3 font-mono text-xs uppercase tracking-widest">· last run failed</span>}
        </div>
        <p className="text-muted-foreground text-sm mt-1">
          Sources: Apple Music Most Played (US, UK, FR, DE, IT, ES, NL, KR, CA, NG, ZA) · Deezer fanbase · Press buzz from XXL, The Source, Billboard, Ebony, Essence, Complex, NME, Official Charts.
          {status?.summary && ` Last run: ${status.summary.artists_updated} artists updated, ${status.summary.charted_names} charting names scanned.`}
          {" "}After each sync a "Biggest Movers" recap is drafted for your approval in Manage Posts.
        </p>
      </div>
      <div className="flex flex-col gap-2">
        <button data-testid="sync-now-btn" onClick={onSync} disabled={syncing || running} className={btnPrimary}>
          {syncing || running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Sync now
        </button>
        <button data-testid="movers-draft-btn" onClick={onMovers} disabled={writing || running || !status?.last_success} className={btnGhost}>
          {writing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PenLine className="w-3.5 h-3.5" />} Write Movers recap
        </button>
      </div>
    </div>
  );
}

function SuggestionCard({ s, onApprove, onDismiss }) {
  const [busy, setBusy] = useState(false);
  const act = async (fn) => { setBusy(true); try { await fn(s); } finally { setBusy(false); } };
  const regions = [...new Set((s.chart_entries || []).map((e) => e.region))].slice(0, 4).join(", ");
  return (
    <div className="border border-white/10 bg-card p-4 flex gap-4 items-start" data-testid={`suggestion-${s.id}`}>
      <div className="w-16 h-16 shrink-0 border border-white/15 overflow-hidden bg-background">
        {s.image ? <img src={s.image} alt={s.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground/40"><Trophy className="w-5 h-5" /></div>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-lg font-medium truncate">{s.name}</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground truncate">
          {s.top_song ? `"${s.top_song}" · ` : ""}{regions}
        </div>
        <div className="mt-2"><ScorePills scores={s.scores} overall={s.overall} /></div>
      </div>
      <div className="flex flex-col gap-2 shrink-0">
        <button data-testid={`approve-${s.id}`} disabled={busy} onClick={() => act(onApprove)} className={btnPrimary}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Add
        </button>
        <button data-testid={`dismiss-${s.id}`} disabled={busy} onClick={() => act(onDismiss)} className={btnGhost}>
          <X className="w-3.5 h-3.5" /> Skip
        </button>
      </div>
    </div>
  );
}

function ArtistForm({ initial, onSave, onDelete, isNew }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await uploadImage(file);
      set("image", url);
      toast.success("Photo uploaded.");
    } catch { toast.error("Upload failed (max 8MB)."); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required."); return; }
    setSaving(true);
    try {
      await onSave(form);
      if (isNew) setForm(emptyArtist());
    } finally { setSaving(false); }
  };

  return (
    <div className={`border bg-card p-5 ${isNew ? "border-signal/40" : "border-white/10"}`} data-testid={isNew ? "artist-new-form" : `artist-edit-${form.id}`}>
      <div className="flex gap-4">
        <div className="shrink-0">
          <div className="w-20 h-20 border border-white/15 overflow-hidden bg-background">
            {form.image ? <img src={mediaUrl(form.image)} alt={form.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground/40"><Trophy className="w-6 h-6" /></div>}
          </div>
          <label className="mt-2 flex items-center justify-center gap-1 cursor-pointer font-mono text-[9px] uppercase tracking-widest border border-white/15 px-2 py-1 hover:border-signal hover:text-signal transition-colors" data-testid={`artist-photo-${form.id || "new"}`}>
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Photo
            <input type="file" accept="image/*" onChange={upload} className="hidden" />
          </label>
        </div>
        <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="col-span-2"><label className={label}>Name</label><input data-testid={`artist-name-${form.id || "new"}`} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Exact artist name (as it appears on charts)" className={inputCls} /></div>
          <div><label className={label}>Title</label><input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Mogul / MC" className={inputCls} /></div>
          <div><label className={label}>City</label><input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Atlanta, USA" className={inputCls} /></div>
          <div className="col-span-2 md:col-span-4"><label className={label}>Bio</label><input value={form.bio} onChange={(e) => set("bio", e.target.value)} placeholder="Short bio" className={inputCls} /></div>
        </div>
      </div>

      {!isNew && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <ScorePills scores={form.scores} overall={form.overall} />
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70">
            {form.top_song ? `Top song: "${form.top_song}"` : "Not charting this month"} · auto-scored
          </span>
        </div>
      )}

      <div className="flex items-center gap-2 mt-4">
        <button data-testid={isNew ? "artist-create-btn" : `artist-save-${form.id}`} onClick={save} disabled={saving} className={btnPrimary}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : isNew ? <Plus className="w-3.5 h-3.5" /> : <Save className="w-3.5 h-3.5" />}
          {isNew ? "Add artist" : "Save"}
        </button>
        {!isNew && (
          <button data-testid={`artist-delete-${form.id}`} onClick={() => onDelete(form.id)} className={btnGhost}>
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        )}
      </div>
    </div>
  );
}

export default function AdminRankings() {
  const [artists, setArtists] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [writing, setWriting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([fetchArtists("overall"), fetchRankingSuggestions(), fetchRankingsSyncStatus()])
      .then(([a, s, st]) => { setArtists(a); setSuggestions(s); setStatus(st); })
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!status?.running) return;
    const t = setInterval(async () => {
      const st = await fetchRankingsSyncStatus().catch(() => null);
      if (st && !st.running) {
        clearInterval(t);
        setSyncing(false);
        toast.success(st.status === "done" ? "Chart sync complete." : "Sync failed — check logs.");
        load();
      } else if (st) setStatus(st);
    }, 4000);
    return () => clearInterval(t);
  }, [status?.running, status?.status, load]);

  const onSync = async () => {
    setSyncing(true);
    await triggerRankingsSync();
    toast("Pulling charts from 11 countries… this takes about a minute.");
    setStatus((s) => ({ ...(s || {}), running: true, status: "running" }));
  };
  const onMovers = async () => {
    setWriting(true);
    try {
      const d = await createMoversDraft();
      toast.success(`Draft ready: "${d.title.length > 70 ? d.title.slice(0, 70) + "…" : d.title}" — approve it in Manage Posts.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not write the recap.");
    } finally { setWriting(false); }
  };
  const onApprove = async (s) => { await approveSuggestion(s.id); toast.success(`${s.name} added to the Power Index.`); load(); };
  const onDismiss = async (s) => { await dismissSuggestion(s.id); setSuggestions((l) => l.filter((x) => x.id !== s.id)); };
  const onApproveAll = async () => { const r = await approveAllSuggestions(); toast.success(`${r.approved} artists added.`); load(); };
  const onRemoveUnranked = async () => { const r = await removeUnrankedArtists(); toast.success(`${r.removed} non-charting artists removed.`); load(); };
  const onCreate = async (form) => { await createArtist(form); toast.success(`${form.name} added and scored.`); load(); };
  const onUpdate = async (form) => { await updateArtist(form.id, form); toast.success(`${form.name} updated.`); load(); };
  const onDelete = async (id) => { await deleteArtist(id); setArtists((a) => a.filter((x) => x.id !== id)); toast.success("Artist removed."); };

  return (
    <div className="max-w-[1200px] mx-auto px-5 lg:px-10 py-16">
      <Link to="/admin/leads" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-signal transition-colors">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to admin
      </Link>
      <div className="mt-4">
        <span className="font-mono text-xs uppercase tracking-[0.3em] text-signal">Internal · Power Index</span>
        <h1 className="font-anton text-5xl md:text-7xl uppercase tracking-tight mt-3 leading-none" data-testid="admin-rankings-title">Rankings Manager</h1>
        <p className="text-muted-foreground text-lg mt-4 max-w-3xl">
          Scores are now fully automatic — pulled from the charts, press and fanbase every month. You just decide who's
          in the index: approve suggested artists below, or add any name manually and it gets scored instantly.
        </p>
      </div>

      <div className="mt-10"><SyncPanel status={status} onSync={onSync} syncing={syncing} onMovers={onMovers} writing={writing} /></div>

      <div className="flex items-end justify-between gap-4 mt-14 mb-4">
        <h2 className="font-anton text-2xl uppercase tracking-tight">
          Suggested additions {suggestions.length ? `(${suggestions.length})` : ""}
        </h2>
        {suggestions.length > 0 && (
          <button data-testid="approve-all-btn" onClick={onApproveAll} className={btnGhost}>
            <Check className="w-3.5 h-3.5" /> Add all {suggestions.length}
          </button>
        )}
      </div>
      <p className="text-muted-foreground text-sm mb-4">Artists charting this month who aren't in your index yet, ranked by score.</p>
      {loading ? (
        <p className="font-mono text-sm text-muted-foreground">Loading…</p>
      ) : suggestions.length === 0 ? (
        <p className="font-mono text-sm text-muted-foreground border border-white/10 p-5" data-testid="suggestions-empty">
          No pending suggestions. Run a sync to scan the charts for new names.
        </p>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {suggestions.map((s) => <SuggestionCard key={s.id} s={s} onApprove={onApprove} onDismiss={onDismiss} />)}
        </div>
      )}

      <h2 className="font-anton text-2xl uppercase tracking-tight mt-14 mb-4">Add an artist manually</h2>
      <ArtistForm initial={emptyArtist()} onSave={onCreate} isNew />

      <div className="flex items-end justify-between gap-4 mt-14 mb-4">
        <h2 className="font-anton text-2xl uppercase tracking-tight">
          Current index {artists.length ? `(${artists.length})` : ""}
        </h2>
        {status?.unranked > 0 && (
          <button data-testid="remove-unranked-btn" onClick={onRemoveUnranked} className={btnGhost}>
            <Eraser className="w-3.5 h-3.5" /> Remove {status.unranked} non-charting
          </button>
        )}
      </div>
      {loading ? (
        <p className="font-mono text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-5">
          {artists.map((a) => (
            <ArtistForm key={a.id} initial={{ ...a }} onSave={onUpdate} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  );
}
